/**
 * Work Order Create Page.
 *
 * Two-mode creation flow:
 * 1. **From Scratch** — blank form for a fully custom work order.
 * 2. **From Template** — pick a recurring WO template to pre-fill the
 *    title, work type, priority, description, and attached forms.
 *
 * Both modes let the user pick an asset and override any pre-filled values.
 * A mode selector appears at the top when templates exist; if there are no
 * templates, the page goes straight to the blank form.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "@/components/ui/UnsavedChangesDialog";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useCreateWorkOrder } from "@/features/work-orders/api";
import { toast } from "@/stores/toastStore";
import { useAssets } from "@/features/inventory/api";
import { WORK_TYPES, PRIORITIES } from "@/types/work-order";
import SearchSelect from "@/components/ui/SearchSelect";
import { Package, Save } from "lucide-react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import type { WorkOrderCreate } from "@/types/work-order";
import {
  Wrench,
  FileText,
  ClipboardList,
  ChevronRight,
  Check,
  AlertTriangle,
} from "lucide-react";

type Mode = "choose" | "scratch" | "template";

interface WOTemplate {
  id: string;
  name: string;
  work_type: string;
  title_template: string;
  description: string | null;
  priority: string;
  schedule_value: number;
  form_template_ids: string[] | null;
  is_active: boolean;
  asset_id: string | null;
}

const selectClass =
  "mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]";

const textareaClass =
  "mt-1.5 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]";

function WorkOrderCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedAssetId = searchParams.get("asset_id") || "";
  const skipBlockRef = useRef(false);

  const { data: assetData, isLoading: assetsLoading } = useAssets({
    pageSize: 200,
  });
  const createMutation = useCreateWorkOrder();

  // Fetch templates for the "from template" mode.
  const { data: templateData } = useQuery<WOTemplate[]>({
    queryKey: ["recurring-wos-for-create"],
    queryFn: async () => {
      const res = await apiClient.get("/api/v1/recurring-wos?page_size=100");
      // The endpoint may return { items: [...] } or a plain array.
      const d = res.data;
      return Array.isArray(d) ? d : d.items || [];
    },
  });

  const templates = (templateData || []).filter((t) => t.is_active);

  // Mode: if templates exist, start at "choose"; otherwise go straight to scratch.
  const [mode, setMode] = useState<Mode>("choose");
  const [selectedTemplate, setSelectedTemplate] = useState<WOTemplate | null>(
    null,
  );

  // Form state.
  const [assetId, setAssetId] = useState(preselectedAssetId);
  const [title, setTitle] = useState("");
  const [workType, setWorkType] = useState("repair");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // --- Unsaved changes protection ---
  const isDirty =
    !skipBlockRef.current &&
    (assetId !== "" ||
      title !== "" ||
      description !== "" ||
      estimatedHours !== "" ||
      notes !== "");

  const { isBlocked, proceed, reset, checkUnsaved } =
    useUnsavedChanges(isDirty);

  const saveDraft = async () => {
    if (!assetId || !title) {
      toast.error("Asset and title are required to save draft");
      return;
    }
    skipBlockRef.current = true;
    try {
      const payload: WorkOrderCreate = {
        asset_id: assetId,
        title,
        work_type: workType,
        priority,
        description: description || undefined,
        estimated_hours: estimatedHours
          ? parseFloat(estimatedHours)
          : undefined,
        notes: notes || undefined,
        form_template_ids: selectedTemplate?.form_template_ids?.length
          ? selectedTemplate.form_template_ids
          : undefined,
      };

      const wo = await createMutation.mutateAsync(payload);
      navigate(`/work-orders/${wo.id}`);
    } catch {
      skipBlockRef.current = false;
      toast.error("Failed to save draft");
    }
  };

  // When there are no templates, skip the chooser.
  useEffect(() => {
    if (templateData && templates.length === 0 && mode === "choose") {
      setMode("scratch");
    }
  }, [templateData, templates.length, mode]);

  // Apply template values to the form.
  const applyTemplate = (tmpl: WOTemplate) => {
    setSelectedTemplate(tmpl);
    const today = new Date().toISOString().slice(0, 10);
    setTitle(tmpl.title_template.replace("{date}", today));
    setWorkType(tmpl.work_type);
    setPriority(tmpl.priority);
    setDescription(tmpl.description || "");
    if (tmpl.asset_id) setAssetId(tmpl.asset_id);
    setMode("template");
  };

  // Reset to blank form.
  const startFromScratch = () => {
    setSelectedTemplate(null);
    setTitle("");
    setWorkType("repair");
    setPriority("medium");
    setDescription("");
    setEstimatedHours("");
    setNotes("");
    if (!preselectedAssetId) setAssetId("");
    setMode("scratch");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!assetId) {
      setError("Please select an asset.");
      return;
    }

    try {
      const payload: WorkOrderCreate = {
        asset_id: assetId,
        title,
        work_type: workType,
        priority,
        description: description || undefined,
        estimated_hours: estimatedHours
          ? parseFloat(estimatedHours)
          : undefined,
        notes: notes || undefined,
        form_template_ids: selectedTemplate?.form_template_ids?.length
          ? selectedTemplate.form_template_ids
          : undefined,
      };

      const wo = await createMutation.mutateAsync(payload);
      skipBlockRef.current = true;
      navigate(`/work-orders/${wo.id}`);
    } catch {
      setError("Failed to create work order.");
    }
  };

  // ------------------------------------------------------------------
  // Mode: Choose (template vs. scratch)
  // ------------------------------------------------------------------
  if (mode === "choose") {
    return (
      <div className="page-form space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            New Work Order
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Start from scratch or use a template to pre-fill the work order.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* From Scratch */}
          <button
            type="button"
            onClick={startFromScratch}
            className="group relative flex flex-col items-start gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-base)] p-5 text-left transition-all hover:border-[var(--color-interactive-primary)]/40 hover:shadow-[var(--shadow-md)] active:scale-[0.98]"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)]/10">
              <Wrench className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                From Scratch
              </h3>
              <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
                Create a blank work order and fill in all details manually.
              </p>
            </div>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5" />
          </button>

          {/* From Template */}
          <button
            type="button"
            onClick={() => setMode("template")}
            className="group relative flex flex-col items-start gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-base)] p-5 text-left transition-all hover:border-[var(--color-interactive-primary)]/40 hover:shadow-[var(--shadow-md)] active:scale-[0.98]"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--fill-success)]">
              <ClipboardList className="h-5 w-5 text-[var(--text-success)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                From Template
              </h3>
              <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
                Pre-fill from a work order template ({templates.length}{" "}
                available).
              </p>
            </div>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Mode: Template picker (before form)
  // ------------------------------------------------------------------
  if (mode === "template" && !selectedTemplate) {
    return (
      <div className="page-form space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMode("choose")}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-raised)] transition-colors"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">
              Choose a Template
            </h2>
            <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
              Select a template to pre-fill the work order details.
            </p>
          </div>
        </div>

        {templates.length > 0 ? (
          <div className="space-y-2 stagger-children">
            {templates.map((tmpl) => {
              const workTypeLabel =
                WORK_TYPES.find((t) => t.value === tmpl.work_type)?.label ||
                tmpl.work_type;
              const priorityColor =
                tmpl.priority === "urgent"
                  ? "text-[var(--text-danger)]"
                  : tmpl.priority === "high"
                    ? "text-[var(--text-warning)]"
                    : "text-[var(--text-tertiary)]";

              return (
                <button
                  key={tmpl.id}
                  type="button"
                  onClick={() => applyTemplate(tmpl)}
                  className="group w-full text-left"
                >
                  <Card
                    padding="sm"
                    className="transition-all hover:border-[var(--color-interactive-primary)]/40 hover:shadow-[var(--shadow-sm)] active:scale-[0.99]"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--fill-success)] text-[var(--text-success)]">
                        <ClipboardList className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-[var(--text-primary)]">
                            {tmpl.name}
                          </span>
                          <span className="text-[10px] rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-2 py-0.5 font-medium text-[var(--text-secondary)]">
                            {workTypeLabel}
                          </span>
                          <span
                            className={`flex items-center gap-0.5 text-[10px] font-medium ${priorityColor}`}
                          >
                            {tmpl.priority === "urgent" && (
                              <AlertTriangle className="h-3 w-3" />
                            )}
                            {tmpl.priority}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-[var(--text-tertiary)] truncate">
                          Title: "{tmpl.title_template}"
                          {tmpl.form_template_ids?.length
                            ? ` · ${tmpl.form_template_ids.length} form${tmpl.form_template_ids.length !== 1 ? "s" : ""} attached`
                            : ""}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>
        ) : (
          <Card>
            <div className="flex flex-col items-center py-8">
              <FileText className="h-10 w-10 text-[var(--text-tertiary)]" />
              <p className="mt-3 text-sm text-[var(--text-secondary)]">
                No active templates found.
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3"
                onClick={startFromScratch}
              >
                Create from scratch instead
              </Button>
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Mode: Form (scratch or template-prefilled)
  // ------------------------------------------------------------------
  return (
    <div className="page-form space-y-6">
      <div className="flex items-center gap-3">
        {templates.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setSelectedTemplate(null);
              setMode("choose");
            }}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-raised)] transition-colors"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
          </button>
        )}
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            New Work Order
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {selectedTemplate ? (
              <>
                Using template: <strong>{selectedTemplate.name}</strong>. You
                can modify any field below.
              </>
            ) : (
              "Create a job for repair, maintenance, or refurbishment."
            )}
          </p>
        </div>
      </div>

      {/* Template badge */}
      {selectedTemplate && (
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--text-success)]/20 bg-[var(--fill-success)] px-4 py-2.5">
          <Check className="h-4 w-4 text-[var(--text-success)]" />
          <span className="text-sm font-medium text-[var(--text-success)]">
            Pre-filled from "{selectedTemplate.name}"
          </span>
          {selectedTemplate.form_template_ids?.length ? (
            <span className="ml-auto text-xs text-[var(--text-success)]/70">
              <FileText className="inline h-3 w-3 mr-0.5" />
              {selectedTemplate.form_template_ids.length} form
              {selectedTemplate.form_template_ids.length !== 1 ? "s" : ""} will
              be attached
            </span>
          ) : null}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-[var(--radius-md)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]">
            {error}
          </div>
        )}

        <Card>
          <h3 className="mb-4 text-sm font-semibold text-[var(--text-primary)]">
            Work Order Details
          </h3>
          <div className="space-y-4">
            {/* Asset selector — searchable autocomplete */}
            <SearchSelect
              label="Asset"
              required
              value={assetId}
              onChange={setAssetId}
              placeholder="Search by SKU, name, or category..."
              isLoading={assetsLoading}
              options={(assetData?.items ?? []).map((a) => ({
                value: a.id,
                label: `${a.sku} — ${a.name}`,
                sublabel: `${a.category} · ${a.status}${a.location ? ` · ${a.location}` : ""}`,
                icon: (
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--surface-sunken)]">
                    <Package className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
                  </div>
                ),
              }))}
            />

            {/* Title — full width */}
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g., Engine Overhaul"
            />

            {/* Work type + Priority */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Work Type
                </label>
                <select
                  value={workType}
                  onChange={(e) => setWorkType(e.target.value)}
                  className={selectClass}
                >
                  {WORK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className={selectClass}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Estimated hours */}
            <Input
              label="Estimated Hours"
              type="number"
              step="0.5"
              min="0"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)}
              placeholder="0.0"
            />

            {/* Description */}
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Detailed instructions for the technician"
                className={textareaClass}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional internal notes"
                className={textareaClass}
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => checkUnsaved(() => navigate("/work-orders"))}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={saveDraft}
          >
            <Save className="h-3.5 w-3.5" /> Save Draft
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            Create Work Order
          </Button>
        </div>
      </form>

      <UnsavedChangesDialog
        isOpen={isBlocked}
        onSaveDraft={saveDraft}
        onDiscard={proceed}
        onStay={reset}
        entityName="work order"
      />
    </div>
  );
}

export default WorkOrderCreatePage;
